<script setup lang="ts">
import { computed } from "vue"
import DrinkDiagram, { type DrinkLayer } from "./DrinkDiagram.vue"

type Ingredient = {
    id: number
    name: string
    unit: string
    quantity: number
    note: string | null
}

const props = defineProps<{
    recipeId: number | string
    name: string
    code?: string | null
    category: string
    size?: string | null
    isIced: boolean
    isFood?: boolean
    imageUrl?: string | null
    preparation: string[]
    options: string[]
    layers: DrinkLayer[]
    ingredients: Ingredient[]
}>()

const sizeHeader = computed(() => props.size || (props.isIced ? "22 oz" : "16 oz"))
const foodPlaceholder = "/images/food-placeholder.svg"
const displayImage = computed(() => props.imageUrl || (props.isFood ? foodPlaceholder : null))
const isPlaceholder = computed(() => !props.imageUrl && Boolean(props.isFood))

const prepColumns = computed(() => {
    const steps = props.preparation ?? []
    const split = Math.ceil(steps.length / 2) || 0
    return [steps.slice(0, split), steps.slice(split)]
})

function trimNumber(value: number) {
    return Number.parseFloat(Number(value).toFixed(2)).toString()
}

function pluralize(unit: string, quantity: number) {
    if (quantity === 1) {
        return unit
    }

    const irregular: Record<string, string> = {
        shot: "shots",
        pump: "pumps",
        scoop: "scoops",
        dash: "dashes",
        cup: "cups",
        piece: "pieces",
        slice: "slices",
        pack: "packs",
        sachet: "sachets",
    }

    return irregular[unit] ?? unit
}

function formatQuantity(quantity: number, unit: string) {
    const amount = trimNumber(quantity)

    if (["ml", "g"].includes(unit)) {
        return `${amount} ${unit}`
    }

    return `${amount} ${pluralize(unit, Number(quantity))}`
}

function rowValues(ingredient: Ingredient) {
    if (ingredient.note) {
        return { span: true, text: ingredient.note }
    }

    return {
        span: false,
        amount: formatQuantity(ingredient.quantity, ingredient.unit),
    }
}

const tableRows = computed(() => {
    return props.ingredients.map((ingredient) => ({
        ingredient,
        values: rowValues(ingredient),
    }))
})
</script>

<template>
    <article class="recipe-card relative overflow-hidden rounded-sm border border-[rgb(181_115_5)]/30 bg-white px-6 py-5 sm:px-8 sm:py-6">
        <!-- Binder punch hole -->
        <span class="absolute left-5 top-5 h-6 w-6 rounded-full border-2 border-[#e4b4c4] bg-transparent" />

        <header class="relative mb-4 flex items-start justify-between gap-4 pt-2">
            <div class="pl-8">
                <p class="text-[11px] font-semibold uppercase tracking-[0.22em] text-[rgb(181_115_5)]/70">
                    {{ category }}
                </p>
                <div class="mt-1 flex flex-wrap items-baseline gap-3">
                    <h2 class="font-recipe text-3xl font-bold leading-tight text-[rgb(181_115_5)] sm:text-[2.1rem]">
                        {{ name }}
                    </h2>
                    <span
                        v-if="code && !isFood"
                        class="rounded-sm border border-[rgb(181_115_5)] px-2 py-0.5 font-recipe text-lg font-bold tracking-[0.18em] text-[rgb(181_115_5)]"
                    >
                        {{ code }}
                    </span>
                </div>
            </div>

            <img
                src="/images/antahan-logo.svg"
                alt="Antahan Café"
                class="h-16 w-auto shrink-0 sm:h-[76px]"
            />
        </header>

        <div class="grid items-start gap-4 lg:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)] print:grid-cols-[240px_minmax(0,1fr)]">
            <div class="mx-auto h-[340px] w-full max-w-[280px] lg:mx-0">
                <img
                    v-if="displayImage"
                    :src="displayImage"
                    :alt="name"
                    class="h-full w-full rounded-sm border border-[rgb(181_115_5)]"
                    :class="isPlaceholder ? 'bg-[#f7f7f5] object-contain' : 'object-cover'"
                />
                <DrinkDiagram
                    v-else
                    :recipe-id="recipeId"
                    :is-iced="isIced"
                    :category="category"
                    :layers="layers"
                />
            </div>

            <div class="overflow-hidden rounded-sm border border-[rgb(181_115_5)]">
                <table class="w-full border-collapse text-[13px] text-[#1e3932]">
                    <thead>
                        <tr class="bg-[rgb(181_115_5)] text-left text-white">
                            <th class="px-3 py-2 font-semibold">Ingredients</th>
                            <th class="px-3 py-2 text-center font-semibold">
                                {{ sizeHeader }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="({ ingredient, values }, index) in tableRows"
                            :key="ingredient.id"
                            :class="index % 2 === 0 ? 'bg-[rgb(181_115_5)]/10' : 'bg-[#f7f7f5]'"
                        >
                            <td class="px-3 py-2 font-medium">{{ ingredient.name }}</td>
                            <td v-if="values.span" class="px-3 py-2 text-center italic">
                                {{ values.text }}
                            </td>
                            <td v-else class="px-3 py-2 text-center">{{ values.amount }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div
            class="mt-5 grid gap-4"
            :class="options.length && preparation.length ? 'lg:grid-cols-[minmax(0,1fr)_220px] print:grid-cols-[minmax(0,1fr)_220px]' : ''"
        >
            <section v-if="preparation.length" class="overflow-hidden rounded-sm border border-[rgb(181_115_5)]">
                <h3 class="bg-[rgb(181_115_5)] px-3 py-1.5 text-sm font-semibold text-white">Preparation</h3>
                <div class="grid gap-x-8 gap-y-1 px-4 py-3 text-[13px] text-[rgb(181_115_5)] sm:grid-cols-2">
                    <ol class="list-decimal space-y-1 pl-4">
                        <li v-for="step in prepColumns[0]" :key="step">{{ step }}</li>
                    </ol>
                    <ol class="list-decimal space-y-1 pl-4" :start="prepColumns[0].length + 1">
                        <li v-for="step in prepColumns[1]" :key="step">{{ step }}</li>
                    </ol>
                </div>
            </section>

            <section v-if="options.length" class="overflow-hidden rounded-sm border border-[rgb(181_115_5)]">
                <h3 class="bg-[rgb(181_115_5)] px-3 py-1.5 text-sm font-semibold text-white">Options</h3>
                <ul class="space-y-1 px-4 py-3 text-[13px] text-[rgb(181_115_5)]">
                    <li v-for="option in options" :key="option" class="flex gap-2">
                        <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[rgb(181_115_5)]" />
                        <span>{{ option }}</span>
                    </li>
                </ul>
            </section>
        </div>

        <p class="mt-4 text-center text-[10px] tracking-wide text-gray-400">
            © {{ new Date().getFullYear() }} Antahan Cafe. Confidential. For barista training and in-store use only.
        </p>
    </article>
</template>
